import {
  CANCELABLE_EXECUTION_STATES,
  EXECUTION_STATE,
  STALLABLE_EXECUTION_STATES,
  SUPERVISOR_CLOSABLE_EXECUTION_STATES,
} from '@src/models/execution';
import ExecutionStateRepository from '@src/repositories/ExecutionStateRepository';

class ExecutionStateService {
  constructor() {
    this.repository = new ExecutionStateRepository();
  }

  /**
   * Get execution state by ID
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<ExecutionState|null>} ExecutionState instance or null if not found
   */
  async getExecutionState(organizationId, divisionId, executionId) {
    try {
      const executionState = await this.repository.get(organizationId, divisionId, executionId);
      return executionState;
    } catch (error) {
      throw new Error(`Service error getting execution state: ${error.message}`);
    }
  }

  /**
   * Validate execution state data
   * @param {Object} executionStateData - Execution state data to validate
   * @returns {Object} Object with isValid (boolean) and errors (array)
   */
  static validateExecutionState(executionStateData) {
    const errors = [];

    if (
      executionStateData?.currentState &&
      !CANCELABLE_EXECUTION_STATES.includes(executionStateData?.currentState)
    ) {
      errors.push(`El estado "${executionStateData?.currentState}" no puede ser cancelado`);
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Format execution state data before saving
   * @param {Object} executionStateData - Execution state data
   * @returns {Object} Formatted data
   */
  formatExecutionStateData(executionStateData) {
    return {
      ...executionStateData,
      currentState: executionStateData.currentState?.trim(),
      organizationId: executionStateData.organizationId,
      divisionId: executionStateData.divisionId,
      executionId: executionStateData.executionId,
    };
  }

  /**
   * Cancel an existing execution state
   * @param {Object} executionStateData - Execution state data
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Updated execution state
   */
  async cancel(executionStateData, organizationId, divisionId, executionId) {
    const validation = ExecutionStateService.validateExecutionState(executionStateData);
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
    }

    return this.repository.cancel(organizationId, divisionId, executionId);
  }

  /**
   * Report a delay on an execution
   * @param {Object} executionStateData - Execution state data
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @param {string} reason - Delay justification
   * @returns {Promise<Object>} Result with success status and message
   */
  async reportStall(executionStateData, organizationId, divisionId, executionId, reason) {
    const currentState = executionStateData?.currentState;
    if (currentState && !STALLABLE_EXECUTION_STATES.includes(currentState)) {
      throw new Error(`El estado "${currentState}" no puede ser demorado`);
    }

    if (!reason?.trim()) {
      throw new Error('Debe indicar una justificación de demora');
    }

    return this.repository.reportStall(organizationId, divisionId, executionId, reason.trim());
  }

  /**
   * End the active delay on an execution
   * @param {Object} executionStateData - Execution state data
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object>} Result with success status and message
   */
  async endStall(executionStateData, organizationId, divisionId, executionId) {
    const currentState = executionStateData?.currentState;
    if (currentState && currentState !== EXECUTION_STATE.stalled) {
      throw new Error(`El estado "${currentState}" no tiene una demora activa`);
    }

    return this.repository.endStall(organizationId, divisionId, executionId);
  }

  /**
   * Mark an execution done on behalf of its assignees (supervisor closure, ADR 0002)
   * @param {Object} executionStateData - Execution state data
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @param {string} reason - Why the work was not logged
   * @returns {Promise<Object>} Result with success status and message
   */
  async closeOnBehalf(executionStateData, organizationId, divisionId, executionId, reason) {
    const currentState = executionStateData?.currentState;
    if (!SUPERVISOR_CLOSABLE_EXECUTION_STATES.includes(currentState)) {
      throw new Error(`El estado "${currentState}" no puede ser cerrado por un supervisor`);
    }

    if (!reason?.trim()) {
      throw new Error('Debe indicar el motivo del cierre');
    }

    return this.repository.closeOnBehalf(organizationId, divisionId, executionId, reason.trim());
  }
}

export default ExecutionStateService;
